/**
 * /field/pf/digs — the hand's digs, and /field/pf/digs/:id — the dig sheet
 * (GET /pipeline-facility/field/digs[/:id], fieldiq-api#1219, PF-3a).
 * SNAKE_CASE, like the rest of the field surface.
 *
 * THE SELECTION IS THE API'S: digs on open jobs whose crew the caller is on.
 * The sheet records features found in the ditch and takes the dig's next step
 * from `allowed_next` — minus `reported`, which only issuing the report in the
 * office does. Every refusal (features not assessed, no repair recorded, not
 * on the crew) is the API's sentence, inline, never a success.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, MapPin, Pencil, Plus } from 'lucide-react';
import { usePfFieldDig, usePfFieldDigs, useAddPfFieldFeature, useUpdatePfFieldDig, useUpdatePfFieldFeature } from '../../../hooks/usePfDigs';
import { usePfMeta } from '../../../hooks/usePipelineFacility';
import { fmtNumber, pfDay } from '../../../lib/pipeline-facility';
import { PF_DIG_MOVE_LABEL, coatingText, pfAssessmentSkin, pfDigAwaitsReport, pfDigEditable, pfDigRefusal, pfDigStatusSkin, pfDigSteps, remainingWtText } from '../../../lib/pf-digs';
import type { PfFieldDig, PfFieldDigFeature } from '../../../types/pf-digs';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { FeatureForm } from '../digs/FeatureForm';
import { notOnCrewMessage } from './PfFieldJobScreen';

function oneCall(d: Pick<PfFieldDig, 'one_call_ticket' | 'one_call_valid_to' | 'one_call_expired'>): string {
  if (!d.one_call_ticket && !d.one_call_valid_to) return '—';
  return `${d.one_call_ticket || '—'}${d.one_call_valid_to ? ` · valid to ${pfDay(d.one_call_valid_to)}` : ''}${d.one_call_expired ? ' (expired)' : ''}`;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-right text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

export function PfMyDigsScreen() {
  const query = usePfFieldDigs();
  const digs = query.data?.data ?? [];
  const chainageUnit = query.data?.meta.units?.chainage ?? null;
  return (
    <PtFieldPage title="My digs" backTo="/field/pf" testId="pf-my-digs">
      <PtFieldQueryState query={query} subject="your digs">
        {digs.length === 0 ? (
          <PtFieldEmpty>No digs on your open jobs. The office plans digs on a job you are crewed on.</PtFieldEmpty>
        ) : (
          <div className="space-y-2">
            {digs.map((d) => (
              <Link key={d.id} to={`/field/pf/digs/${d.id}`} className="block" data-testid="pf-my-dig">
                <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-base font-extrabold text-fieldiq-text">{d.dig_number || '—'}</span>
                      <PtFieldBadge skin={pfDigStatusSkin(d.status)} />
                    </div>
                    <p className="mt-1 truncate text-base font-bold text-white">{d.job_number || '—'} · {d.customer_name || '—'}</p>
                    <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
                      <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
                      {d.site_name || '—'} · {fmtNumber(d.chainage)}
                      {d.chainage != null && chainageUnit ? ` ${chainageUnit}` : ''}
                    </p>
                    <p className="mt-1 text-xs text-fieldiq-muted">Ticket {oneCall(d)}</p>
                  </div>
                  <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
                </PtFieldCard>
              </Link>
            ))}
          </div>
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}

function FeatureCard({ f, wtUnit, editable, onEdit }: { f: PfFieldDigFeature; wtUnit: string | null; editable: boolean; onEdit: () => void }) {
  return (
    <div className="border-b border-fieldiq-border px-4 py-3 last:border-b-0" data-testid="pf-field-feature">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-sm font-extrabold text-white">{f.anomaly_feature_id || (f.found_in_ditch ? 'Found in ditch' : '—')}</p>
          <p className="text-sm text-fieldiq-text">{f.feature_type_label || f.feature_type} · {f.clock_position || '—'} o’clock</p>
        </div>
        <PtFieldBadge skin={pfAssessmentSkin(f.assessment_result)} />
      </div>
      <p className="mt-1 text-sm text-fieldiq-text">
        UT min {fmtNumber(f.ut_min_wt)}
        {f.ut_min_wt != null && wtUnit ? ` ${wtUnit}` : ''} · pit {fmtNumber(f.pit_depth)}
        {f.pit_depth != null && wtUnit ? ` ${wtUnit}` : ''}
      </p>
      <p className="mt-0.5 text-sm font-semibold text-white" data-testid="pf-field-feature-remaining">
        Remaining WT {remainingWtText(f.remaining_wt_pct, f.remaining_wt_reason)}
      </p>
      {editable && (
        <button type="button" className={`${PT_FIELD_SECONDARY} mt-2`} onClick={onEdit}>
          <Pencil className="size-4" aria-hidden="true" /> Edit feature
        </button>
      )}
    </div>
  );
}

export function PfFieldDigScreen() {
  const { id } = useParams<{ id: string }>();
  const query = usePfFieldDig(id);
  const catalog = usePfMeta().data?.data ?? null;
  const dig = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const wtUnit = meta?.units?.wall_thickness ?? null;
  const chainageUnit = meta?.units?.chainage ?? null;
  const refused = notOnCrewMessage(query);
  const step = useUpdatePfFieldDig();
  const add = useAddPfFieldFeature();
  const edit = useUpdatePfFieldFeature();
  const [stepError, setStepError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const editable = !!dig && pfDigEditable(dig.status, dig.job_status);
  const steps = editable && dig ? pfDigSteps(dig.allowed_next) : [];

  const takeStep = (to: string) => {
    if (!dig) return;
    setStepError(null);
    step.mutate(
      { id: dig.id, body: { status: to } },
      { onError: (e) => setStepError(pfDigRefusal(e, 'The dig could not move on. Try again.')) },
    );
  };

  return (
    <PtFieldPage title={dig?.dig_number || 'Dig'} backTo="/field/pf/digs" testId="pf-field-dig">
      {refused ? (
        <PtRefusal message={refused} testId="pf-field-dig-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this dig">
          {!dig ? (
            <PtRefusal message="That dig no longer exists. It may have been deleted." />
          ) : (
            <div className="space-y-4">
              <PtFieldCard>
                <div className="flex items-center justify-between gap-2 border-b border-fieldiq-border px-4 py-3">
                  <span className="text-base font-bold text-white">{dig.site_name || '—'}</span>
                  <PtFieldBadge skin={pfDigStatusSkin(dig.status)} testId="pf-field-dig-status" />
                </div>
                <Row label="Job" value={<Link className="underline" to={`/field/pf/jobs/${dig.job_id}`}>{dig.job_number || '—'}</Link>} />
                <Row label="Segment" value={dig.segment_label || '—'} />
                <Row label={`Chainage${chainageUnit ? ` (${chainageUnit})` : ''}`} value={fmtNumber(dig.chainage)} />
                <Row label="One-call / GD ticket" value={oneCall(dig)} />
                <Row label={`Nominal WT${wtUnit ? ` (${wtUnit})` : ''}`} value={fmtNumber(dig.nominal_wt)} />
                <Row label="Excavated" value={pfDay(dig.excavated_on)} />
                <Row label="Coating" value={coatingText(dig.coating_type, dig.coating_condition_label || dig.coating_condition)} />
              </PtFieldCard>

              {dig.one_call_expired && editable && (
                <PtRefusal message="The one-call / ground disturbance ticket has expired. Check with the office before digging." testId="pf-field-onecall-expired" />
              )}

              <section aria-label="Dig status">
                <PtFieldSectionTitle>Next step</PtFieldSectionTitle>
                {steps.length > 0 ? (
                  <div className="space-y-2">
                    {steps.map((to) => (
                      <button
                        key={to}
                        type="button"
                        className={to === steps[0] ? PT_FIELD_PRIMARY : PT_FIELD_SECONDARY}
                        data-testid={`pf-field-dig-step-${to}`}
                        disabled={step.isPending}
                        onClick={() => takeStep(to)}
                      >
                        {PF_DIG_MOVE_LABEL[to] ?? to}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-fieldiq-muted" data-testid="pf-field-dig-no-step">
                    {pfDigAwaitsReport(dig.status)
                      ? 'Backfilled. The office issues the dig report, which closes the dig.'
                      : dig.status === 'reported'
                        ? 'Reported. This dig is closed.'
                        : 'This job is closed. The dig takes no more changes.'}
                  </p>
                )}
                <div className="mt-2">
                  <PtRefusal message={stepError} testId="pf-field-dig-step-refusal" />
                </div>
              </section>

              <section aria-label="Features">
                <PtFieldSectionTitle>Features · {dig.features.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {dig.features.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No features recorded yet.</p>
                  ) : (
                    dig.features.map((f) =>
                      editingId === f.id ? (
                        <FeatureForm
                          key={f.id}
                          initial={f}
                          editing
                          catalog={catalog}
                          wtUnit={wtUnit}
                          pending={edit.isPending}
                          error={editError}
                          submitLabel="Save feature"
                          testId="pf-field-feature-edit"
                          onCancel={() => setEditingId(null)}
                          onSubmit={(body) => {
                            setEditError(null);
                            edit.mutate(
                              { id: f.id, body },
                              { onSuccess: () => setEditingId(null), onError: (e) => setEditError(pfDigRefusal(e, 'The feature could not be saved.')) },
                            );
                          }}
                        />
                      ) : (
                        <FeatureCard key={f.id} f={f} wtUnit={wtUnit} editable={editable} onEdit={() => { setEditError(null); setEditingId(f.id); }} />
                      ),
                    )
                  )}
                </PtFieldCard>
                {editable &&
                  (adding ? (
                    <PtFieldCard className="mt-2">
                      <FeatureForm
                        editing={false}
                        catalog={catalog}
                        wtUnit={wtUnit}
                        pending={add.isPending}
                        error={addError}
                        submitLabel="Add feature"
                        testId="pf-field-feature-add"
                        onCancel={() => setAdding(false)}
                        onSubmit={(body) => {
                          setAddError(null);
                          add.mutate(
                            { digId: dig.id, body },
                            { onSuccess: () => setAdding(false), onError: (e) => setAddError(pfDigRefusal(e, 'The feature could not be added.')) },
                          );
                        }}
                      />
                    </PtFieldCard>
                  ) : (
                    <button type="button" className={`${PT_FIELD_SECONDARY} mt-2`} data-testid="pf-field-add-feature" onClick={() => { setAddError(null); setAdding(true); }}>
                      <Plus className="size-4" aria-hidden="true" /> Add a feature found in the ditch
                    </button>
                  ))}
              </section>

              <section aria-label="Repairs">
                <PtFieldSectionTitle>Repairs · {dig.repairs.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {dig.repairs.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No repairs recorded. The office records repairs on the dig.</p>
                  ) : (
                    dig.repairs.map((r) => <Row key={r.id} label={r.method_label || r.method} value={pfDay(r.repaired_on)} />)
                  )}
                </PtFieldCard>
              </section>
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
